import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpHeaders, HttpRequest, HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { filter, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { GlobalConfigService } from './global-config.service';
import { AddressApiMode } from '@app/models/global-config';

export interface AdressePostale {
  housenumber: string; // numRue
  street: string; // rue
  idStreet?: number; // idRue
  postcode: string; // codePostal
  city: string; // ville
  idCity?: number; // idVille
  label: string; // Adresse complète sur une ligne
  name: string; // Adresse sans la ville (=numRue + rue )
}

@Injectable({
  providedIn: 'root'
})
export class AdresseAutocompleteService {

  /********************************************************************************
  * 🛈 L’API Adresse de la Base Adresse Nationale est transférée à l’IGN
  *    Cela signifie que l'url "api-adresse.data.gouv.fr" sera décommissionnée fin Janvier 2026, et qu'il convient donc
  *    désormais d'utiliser l'url "https://data.geopf.fr/geocodage/search/"
  *  Voir : https://adresse.data.gouv.fr/blog/lapi-adresse-de-la-base-adresse-nationale-est-transferee-a-lign
  ********************************************************************************/
  // apiUrl = 'https://api-adresse.data.gouv.fr/search/';
  apiUrl = 'https://data.geopf.fr/geocodage/search/';
  newApiUrl = '/address-search';

  apiMode: AddressApiMode;

  constructor(
    private http: HttpClient,
    private globalConfigService: GlobalConfigService
  ) {
    this.globalConfigService.config$.subscribe(conf => this.apiMode = conf.addressApiMode);
  }

  searchAddress(query: string): Observable<AdressePostale[]> {
    return this.apiMode === AddressApiMode.DOMINO ? this.searchAddressFromDB(query) : this.searchAddressFromGouvApi(query);
  }

  searchAddressFromGouvApi(query: string, type?: string): Observable<AdressePostale[]> {

    if (query && query.trim().length < 3) { // don't call this api with less than 3 caract because it will respond a 400 / Bad request
      return of([]);
    }

    const headers = new HttpHeaders(
      { 'Content-Type': 'application/x-www-form-urlencoded' }
    );

    let searchUrl = this.apiUrl + '?q=' + query;

    if (type) {
      searchUrl += '&type=' + type;
    }

    const req = new HttpRequest('GET', searchUrl, {}, {
      responseType: 'json',
      headers
    });

    return this.http.request(req).pipe(
      filter(response => response.type !== 0),
      map((res: HttpResponse<any>) => {
        return (
          res.body.features?.map(f => f.properties)
            .map(addr => {
              return {
                housenumber: addr.housenumber,
                street: this.normalizeAddress(addr.street),
                postcode: addr.postcode,
                city: this.normalizeAddress(addr.city),
                idCity: addr.idCity,
                idStreet: addr.idStreet,
                label: this.normalizeAddress(addr.label),
                name: this.normalizeAddress(addr.name)
              } as AdressePostale
            })
        ) || []
      })
    );
  }

  searchAddressFromDB(query: string): Observable<AdressePostale[]> {
    const params = new HttpParams({ fromObject: { q: query } });

    return this.http.get<{ timer: string, adresses: AdressePostale[] }>(this.newApiUrl, { params }).pipe(
      map(x => x.adresses)
    );
  }


  normalizeAddress(input: string): string {
    // Méthode pour "normaliser" une chaine de caractère qui peut contenir des caractères non ascii qui risquent de poser problème lors d'un échange avec un service externe
    // par exemple les adresses renvoyées par https://api-adresse.data.gouv.fr/ peuvent parfois contenir des apostrophes qui ne sont pas de "vraies" apostrophes:
    // - https://api-adresse.data.gouv.fr/search/?q=amiral%20ress => mauvais apostrophe (=> Rue de l’Amiral)
    // - https://api-adresse.data.gouv.fr/search/?q=Oree%20de%20la%20Foret%20la%20gavr (=> l'Oree de la Foret)
    //  ==> et cela pose problème par exemple lors de la signature HMAC pour Paybox

    if (!input) return input;

    return input
      // Normalisation Unicode
      .normalize("NFKC")

      // Apostrophes
      .replace(/[\u2018\u2019\u02BC\u2032\uFF07`]/g, "'")

      // Guillemets doubles
      .replace(/[\u201C\u201D\u201E\u201F\uFF02]/g, '"')

      // Tirets
      .replace(/[\u2010-\u2015\u2212\uFE63]/g, "-")

      // Espaces bizarres
      .replace(/[\u00A0\u2007\u202F]/g, " ")
      .replace(/[\u200B\uFEFF]/g, "")

      // Ligatures
      .replace(/œ/g, "oe")
      .replace(/Œ/g, "OE")
      .replace(/æ/g, "ae")
      .replace(/Æ/g, "AE")

      // Ellipses
      .replace(/\u2026/g, "...")

      // Nettoyage final
      .replace(/\s+/g, " ")
      .trim();
  }

}
